import { StaticQuery, graphql } from 'gatsby';
import React, { FC, useEffect } from 'react';

import BodyText from '@ui/atoms/BodyText';
import StyledLink from '@ui/atoms/StyledLink';

import { isBrowser } from '@utils/isBrowser';

import theme from '@theme/WsTheme';

import * as $ from './Footer.styled';
import LinkGroup from './LinkGroup';
import FacebookIcon from './icons/Facebook.icon';
import InstaIcon from './icons/Insta.icon';
import TwitterIcon from './icons/Twitter.icon';

const WSFooterQuery = graphql`
	query WSFooterQuery {
		menus: allButterCollection(filter: { key: { eq: "menu" } }) {
			nodes {
				value {
					menu_key
					link_group {
						link_group_key
					}
				}
			}
		}
		variants: allButterCollection(filter: { key: { eq: "footer_band" } }) {
			nodes {
				value {
					title
					label
					image
					image_alt
					link {
						url
						target
					}
					pages
					default
				}
			}
		}
	}
`;

interface IFolxFooterData {
	menus: any;
	variants: any;
}

interface IFooterBand {
	default: boolean;
	title: string;
	label: string;
	image: string;
	image_alt: string;
	link: {
		url: string;
		target: string;
	};
	pages: string;
}

interface IFolxFooterProps {
	pageData?: IFolxFooterData;
	onCTAClick?: (val: boolean) => void;
}

interface IFooterMenuProps {
	link_group: any;
	menu_key: string;
}

const parsePathname = (pathname: string) => {
	const parsed = pathname.replace(' ', '').split('/').filter(Boolean).join('/');
	return `/${parsed}/`;
};

const variantIsEmpty = (variant: IFooterBand) => {
	const { title, label, image, link } = variant;
	return [title, label, image, link].includes('');
};

const FolxFooter: FC<IFolxFooterProps> = ({
	pageData = {
		menus: null,
		variants: null,
	},
	onCTAClick = () => null,
}) => {
	const { menus, variants } = pageData;
	const variantsList = variants.map((variant: IFooterBand) => {
		let pages = variant.pages.split('\r\n').filter(Boolean);
		pages = pages.map((page) => parsePathname(page));
		return { ...variant, pages: pages };
	});

	const defaultVariant =
		variantsList.find((variant: IFooterBand) => variant.default) ||
		variantsList.find((variant: IFooterBand) => !variantIsEmpty(variant));

	if (!isBrowser) return null;
	useEffect(() => window.scrollTo(0, 0), []);
	const currentPage = new URL(window.location.href);

	const selectedVariant =
		variantsList.find((variant: IFooterBand) =>
			variant.pages.includes(parsePathname(currentPage?.pathname))
		) || defaultVariant;

	const footerMenu = menus.find((m: IFooterMenuProps) => m.menu_key === 'footer');

	return (
		<$.Footer>
			{/* {selectedVariant && <FooterBand data={selectedVariant} />} */}
			<$.FooterSection>
				<$.FooterContainer>
					<$.LinkColumns>
						{footerMenu &&
							footerMenu.link_group.map((menu: any, i: number) => (
								<LinkGroup key={i} menu={menu} />
							))}
					</$.LinkColumns>
					<$.SocialIcons>
						<$.SocialIconWrapper>
							<a href="https://facebook.com/folxhealth" rel="external" aria-label="Facebook">
								<FacebookIcon color={theme.colors.black} bgcolor={theme.colors.yellow} />
							</a>
						</$.SocialIconWrapper>
						<$.SocialIconWrapper>
							<a href="https://instagram.com/folxhealth" rel="external" aria-label="Instagram">
								<InstaIcon color={theme.colors.black} bgcolor={theme.colors.yellow} />
							</a>
						</$.SocialIconWrapper>
						<$.SocialIconWrapper>
							<a href="https://twitter.com/folxhealth" rel="external" aria-label="Twitter">
								<TwitterIcon color={theme.colors.black} bgcolor={theme.colors.yellow} />
							</a>
						</$.SocialIconWrapper>
					</$.SocialIcons>
				</$.FooterContainer>
			</$.FooterSection>
			<$.FooterBottomBar>
				<$.FooterBottomContainer>
					<$.LegalLinks>
						<StyledLink to={`${process.env.GATSBY_MARKETING_SITE_BASE_URL}/legal/tos`}>
							<BodyText size="xs">Terms of Service</BodyText>
						</StyledLink>
						<StyledLink to={`${process.env.GATSBY_MARKETING_SITE_BASE_URL}/legal/privacy`}>
							<BodyText size="xs">Privacy Policy</BodyText>
						</StyledLink>
					</$.LegalLinks>
					<$.AddressContainer>
						<BodyText size="sm"> One Boston Place, Boston, MA 02108</BodyText>
					</$.AddressContainer>
				</$.FooterBottomContainer>
			</$.FooterBottomBar>
		</$.Footer>
	);
};

const FolxFooterData: FC = (props) => {
	return (
		<StaticQuery
			query={WSFooterQuery}
			render={({
				menus: {
					nodes: [menus],
				},
				variants: {
					nodes: [variants],
				},
			}) => (
				<FolxFooter
					pageData={{
						menus: menus.value,
						variants: variants.value,
					}}
					{...props}
				/>
			)}
		/>
	);
};

export default FolxFooterData;
